import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, Home, Bike, UtensilsCrossed, Church, Anchor } from 'lucide-react';
import SEO from '../components/SEO';

const LesPortesEnRe = () => {
  return (
    <>
      <SEO 
        title="Les Portes-en-Ré - Village Authentique Île de Ré | Villa Nomade"
        description="Découvrez Les Portes-en-Ré, village authentique au nord de l'Île de Ré. Location villa de luxe à proximité immédiate du port, des plages et des commerces."
        keywords="Les Portes-en-Ré, village Île de Ré, location villa Les Portes, hébergement Les Portes-en-Ré, port Les Portes"
        ogImage="/images/villa/villa-bedroom-v2.jpg"
      />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/seo/portes-re-v2.jpg)',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Les Portes-en-Ré
            </h1>
            <p className="text-lg sm:text-xl mb-8 text-gray-200">
              Village authentique au charme préservé au cœur de l'Île de Ré
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-[#c17a3a] hover:bg-[#a66830] text-white">
                Réserver votre séjour
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Contenu Principal */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction */}
        <section className="mb-16">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-6 h-6 text-[#c17a3a]" />
            <h2 className="text-3xl font-bold text-gray-900">Pourquoi choisir Les Portes-en-Ré ?</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-lg leading-relaxed mb-6">
              <strong>Les Portes-en-Ré</strong> est un village pittoresque situé à l'extrémité nord-ouest de l'Île de Ré, 
              en Charente-Maritime. Réputé pour son authenticité et son charme préservé, ce village de pêcheurs offre 
              une atmosphère paisible loin de l'agitation touristique, tout en restant proche de toutes les commodités.
            </p>
            <p className="text-lg leading-relaxed mb-6">
              Notre <strong>Villa Nomade</strong> se situe idéalement aux Portes-en-Ré, vous permettant de profiter 
              pleinement du village et de ses environs : port de plaisance animé, plages de sable fin, pistes cyclables, 
              restaurants de fruits de mer, et marchés locaux authentiques.
            </p>
          </div>
        </section>

        {/* Points d'intérêt */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Les incontournables du village</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <Anchor className="w-10 h-10 text-[#c17a3a] mb-4" />
              <h3 className="text-xl font-semibold mb-3">Le Port de Plaisance</h3>
              <p className="text-gray-700">
                Le port des Portes-en-Ré est le cœur vibrant du village. Admirez les bateaux colorés, 
                dégustez des huîtres fraîches sur le port, et profitez de l'ambiance maritime authentique. 
                En été, le port s'anime avec son marché nocturne réputé.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <Church className="w-10 h-10 text-[#c17a3a] mb-4" />
              <h3 className="text-xl font-semibold mb-3">Église Saint-Pierre</h3>
              <p className="text-gray-700">
                Cette église classée du XVe siècle, avec son clocher noir et blanc caractéristique, 
                est un repère visible de loin. Elle témoigne du riche patrimoine historique de l'île 
                et mérite une visite pour son architecture remarquable.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <Bike className="w-10 h-10 text-[#c17a3a] mb-4" />
              <h3 className="text-xl font-semibold mb-3">Pistes Cyclables</h3>
              <p className="text-gray-700">
                Les Portes-en-Ré est le point de départ idéal pour explorer l'île à vélo. 
                Plus de 100 km de pistes cyclables sécurisées vous emmènent à travers marais salants, 
                forêts de pins et villages pittoresques. Location de vélos disponible au village.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
              <UtensilsCrossed className="w-10 h-10 text-[#c17a3a] mb-4" />
              <h3 className="text-xl font-semibold mb-3">Restaurants & Marchés</h3>
              <p className="text-gray-700">
                Le village regorge d'excellents restaurants de fruits de mer et de cuisine régionale. 
                Ne manquez pas le marché du dimanche matin pour découvrir les produits locaux : 
                huîtres, sel de Ré, pommes de terre primeur AOP, vins de Charente.
              </p>
            </div>
          </div>
        </section>

        {/* Plages à proximité */}
        <section className="mb-16 bg-amber-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Plages à proximité</h2>
          <div className="space-y-4 text-gray-700">
            <div className="flex gap-4">
              <div className="font-semibold min-w-[200px]">Plage de la Redoute</div>
              <div>À 2 km - Plage familiale surveillée l'été, idéale pour les enfants</div>
            </div>
            <div className="flex gap-4">
              <div className="font-semibold min-w-[200px]">Plage du Trousse-Chemise</div>
              <div>À 3 km - L'une des plus belles plages de l'île, eau cristalline</div>
            </div>
            <div className="flex gap-4">
              <div className="font-semibold min-w-[200px]">Plage des Gollandières</div>
              <div>À 5 km - Plage sauvage préservée, parfaite pour la tranquillité</div>
            </div>
          </div>
        </section>

        {/* Villa Nomade CTA */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <Home className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Villa Nomade - Votre Havre aux Portes-en-Ré</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Séjournez dans notre villa d'exception située au cœur des Portes-en-Ré. 
            4 chambres, spa de nage, jardin paysager. À 5 minutes à pied du port et des commerces.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">
                Réserver maintenant
              </Button>
            </Link>
            <Link to="/photos">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Voir la villa
              </Button>
            </Link>
          </div>
        </section>

        {/* Informations pratiques */}
        <section className="mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Informations pratiques</h2>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold mb-4">Accès aux Portes-en-Ré</h3>
            <ul className="space-y-2 text-gray-700">
              <li>🚗 <strong>En voiture :</strong> 35 km depuis La Rochelle (30 minutes)</li>
              <li>🚆 <strong>Gare la plus proche :</strong> La Rochelle (TGV Paris-La Rochelle en 3h)</li>
              <li>✈️ <strong>Aéroport :</strong> La Rochelle - Île de Ré (40 minutes)</li>
              <li>🚌 <strong>Bus :</strong> Ligne régulière La Rochelle ↔ Les Portes-en-Ré</li>
            </ul>
            
            <h3 className="text-xl font-semibold mb-4 mt-6">Meilleures périodes</h3>
            <p className="text-gray-700">
              <strong>Haute saison :</strong> Juillet-Août (animation maximale, réservation anticipée recommandée)<br/>
              <strong>Saison idéale :</strong> Mai-Juin et Septembre (temps agréable, moins de monde)<br/>
              <strong>Hors saison :</strong> Octobre-Avril (calme, authenticité, tarifs avantageux)
            </p>
          </div>
        </section>
      </div>

      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristDestination",
          "name": "Les Portes-en-Ré",
          "description": "Village authentique au nord de l'Île de Ré avec port de plaisance, plages et patrimoine",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Les Portes-en-Ré",
            "addressRegion": "Charente-Maritime",
            "postalCode": "17880",
            "addressCountry": "FR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "46.2403",
            "longitude": "-1.5164"
          }
        })}
      </script>
    </>
  );
};

export default LesPortesEnRe;
